import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Clock3,
  LoaderCircle,
  Pause,
  Play,
  RefreshCw,
  ShieldAlert,
  SkipForward,
} from "lucide-react";
import type {
  CleanupJobView,
  CleanupStage,
  CleanupStepView,
} from "@ubisafe/shared";

export const CLEANUP_AUTO_ADVANCE_SECONDS = 5;

interface CleanupProgressPanelProps {
  job: CleanupJobView;
  readOnly?: boolean;
  busyAction?: string | null;
  retryEnabled: boolean;
  onConfirmStep: (step: CleanupStepView) => void;
  onRetryItem: (itemId: string) => void;
  onIgnoreItem: (itemId: string) => void;
  onRetryJob: () => void;
  onResumeJob: () => void;
  onToggleAutoAdvance: (paused: boolean) => void;
  onMarkClean: () => void;
  /** Permite desabilitar o timer real nos testes. */
  autoAdvanceSeconds?: number;
}

export const cleanupStageLabels: Record<CleanupStage, string> = {
  PRECHECK: "Conferindo pré-condições",
  INVENTORY: "Mapeando dados",
  PARTITION_HABIT_DEVIATION_OVERRIDES: "Desvios de hábito das partições",
  OFFICE_HOURS: "Horários de funcionamento",
  OCCURRENCE_SCHEDULES: "Agendas de ocorrências",
  ACCOUNT_SUSPENSIONS: "Suspensões de contas",
  ACCOUNT_PAYMENT_METHODS: "Métodos de pagamento",
  CONTACT_MESSAGE_CONFIGURATIONS: "Configurações de mensagens",
  PATRIMONY_INTRUSIONS: "Intrusões de patrimônios",
  PARTITION_CONTACT_LINKS: "Vínculos de contatos das partições",
  DEVICES: "Dispositivos",
  PGMS: "PGMs",
  PERIODIC_EVENT_CONTROLS: "Controles de eventos periódicos",
  PARTITIONS: "Partições",
  CENTRALS: "Centrais",
  CONTACT_ACCOUNT_LINKS: "Vínculos de contatos com contas",
  ACCOUNTS: "Contas",
  PATRIMONIES: "Patrimônios",
  CONTACTS: "Contatos",
  CUSTOMERS: "Clientes",
  SERVICE_ORDER_AUTOMATIONS: "Automação de OS (legado)",
  HOLIDAYS: "Feriados (legado)",
  VERIFICATION: "Verificação final",
  COMPLETED: "Concluída",
};

const entityLabels: Record<string, string> = {
  PARTITION_HABIT_DEVIATION_OVERRIDE: "desvio de hábito da partição",
  OFFICE_HOURS: "horário de funcionamento",
  OCCURRENCE_SCHEDULE: "agenda de ocorrência",
  ACCOUNT_SUSPENSION: "suspensão de conta",
  ACCOUNT_PAYMENT_METHOD: "método de pagamento",
  CONTACT_MESSAGE_CONFIGURATION: "configuração de mensagens",
  PATRIMONY_INTRUSION: "intrusão de patrimônio",
  PARTITION_CONTACT_LINK: "vínculo de contato da partição",
  DEVICE: "dispositivo",
  PGM: "PGM",
  PERIODIC_EVENT_CONTROL: "controle de evento periódico",
  PARTITION: "partição",
  CENTRAL: "central da conta",
  CONTACT_ACCOUNT_LINK: "vínculo de contato com conta",
  ACCOUNT: "conta",
  PATRIMONY: "patrimônio",
  CONTACT: "contato",
  CUSTOMER: "cliente",
  SERVICE_ORDER_AUTOMATION: "automação de OS legada",
  HOLIDAY: "feriado legado",
};

const activeStatuses = new Set(["PENDING", "PRECHECKING", "INVENTORY", "RUNNING", "VERIFYING"]);

function statusMessage(job: CleanupJobView): string {
  switch (job.status) {
    case "PENDING": return "Aguardando o processamento inicial";
    case "PRECHECKING": return "Validando a POC e as integrações";
    case "INVENTORY": return "Mapeando exclusões e desvinculações necessárias";
    case "AWAITING_CONFIRMATION": return "Aguardando sua confirmação para a próxima etapa";
    case "RUNNING": return "Processando os registros da etapa confirmada";
    case "PAUSED_ITEM_ERROR": return "Pausada: um registro precisa de decisão";
    case "WAITING_SERVICE": return "Aguardando o Serviço de limpeza ficar disponível";
    case "FAILED": return "O processamento foi interrompido";
    case "VERIFYING": return "Verificando os grupos concluídos";
    case "VERIFICATION_FAILED": return "A verificação final falhou";
    case "COMPLETED_WITH_PENDING": return "Concluída com registros pendentes";
    case "COMPLETED": return job.completionMode === "MANUAL_WITH_PENDING"
      ? "Ambiente marcado como limpo com pendências registradas"
      : "POC encerrada após verificação integral";
  }
}

function stepIcon(step: CleanupStepView) {
  if (step.status === "COMPLETED") return <CheckCircle2 aria-hidden="true" />;
  if (step.status === "COMPLETED_WITH_PENDING") return <ShieldAlert aria-hidden="true" />;
  if (step.status === "SKIPPED_EMPTY") return <SkipForward aria-hidden="true" />;
  if (step.status === "RUNNING" || step.status === "CONFIRMED") return <LoaderCircle aria-hidden="true" />;
  if (step.status === "AWAITING_CONFIRMATION") return <Clock3 aria-hidden="true" />;
  return <CircleDashed aria-hidden="true" />;
}

function countText(step: CleanupStepView): string {
  if (step.status === "SKIPPED_EMPTY") return step.stage === "PARTITIONS"
    ? "Nenhuma ação sobre partições neste plano"
    : "Nenhum registro encontrado";
  // Rótulo neutro para não reclassificar visualmente jobs concluídos sob a política antiga.
  const action = step.stage === "PARTITIONS" || step.stage === "CENTRALS" ? "processadas" : "removidos";
  const parts = [`${step.completedCount} de ${step.totalCount} ${action}`];
  if (step.ignoredCount > 0) parts.push(`${step.ignoredCount} ignorados`);
  if (step.failedCount > 0) parts.push(`${step.failedCount} com erro`);
  return parts.join(" · ");
}

function confirmationAction(stage: CleanupStage): string {
  if (stage === "PARTITIONS") return "a preservar, sem excluir";
  if (stage === "CENTRALS") return "a desvincular das contas";
  return "a remover";
}

export function CleanupProgressPanel({
  job,
  readOnly = false,
  busyAction = null,
  retryEnabled,
  onConfirmStep,
  onRetryItem,
  onIgnoreItem,
  onRetryJob,
  onResumeJob,
  onToggleAutoAdvance,
  onMarkClean,
  autoAdvanceSeconds = CLEANUP_AUTO_ADVANCE_SECONDS,
}: CleanupProgressPanelProps) {
  const progress = job.progressTotal > 0
    ? Math.min(100, Math.round((job.progressCompleted / job.progressTotal) * 100))
    : job.status === "COMPLETED" ? 100 : 0;
  const failed = job.status === "FAILED" || job.status === "VERIFICATION_FAILED";
  const paused = job.status === "PAUSED_ITEM_ERROR";
  const waitingService = job.status === "WAITING_SERVICE";
  const waiting = waitingService || job.status === "AWAITING_CONFIRMATION";
  const completed = job.status === "COMPLETED";
  const withPending = job.status === "COMPLETED_WITH_PENDING";
  const ActiveIcon = completed ? CheckCircle2 : failed || paused || withPending ? AlertTriangle : waiting ? Clock3 : LoaderCircle;
  const steps = (job.steps ?? []).filter(
    (step) => step.stage !== "HOLIDAYS" && step.stage !== "SERVICE_ORDER_AUTOMATIONS",
  );
  const awaitingStep = steps.find((step) => step.status === "AWAITING_CONFIRMATION");
  const failedItem = job.failedItem ?? null;
  const isBusy = Boolean(busyAction);

  // Auto-avanço: contagem regressiva local (frontend). O estado "pausado" é
  // compartilhado entre instâncias via job.autoAdvancePaused (persistido no backend).
  const autoAdvanceActive =
    Boolean(awaitingStep) && !readOnly && !job.autoAdvancePaused && !isBusy;
  const [remaining, setRemaining] = useState(Math.max(0, autoAdvanceSeconds));
  const confirmRef = useRef(onConfirmStep);
  confirmRef.current = onConfirmStep;

  // Reinicia a contagem sempre que a etapa aguardando muda ou o auto-avanço (re)ativa.
  useEffect(() => {
    if (!autoAdvanceActive) return;
    setRemaining(Math.max(0, autoAdvanceSeconds));
  }, [autoAdvanceActive, awaitingStep?.stage, autoAdvanceSeconds]);

  useEffect(() => {
    if (!autoAdvanceActive || !awaitingStep || autoAdvanceSeconds <= 0) return;
    const step = awaitingStep;
    let left = autoAdvanceSeconds;
    const timer = window.setInterval(() => {
      left -= 1;
      setRemaining(Math.max(0, left));
      if (left <= 0) {
        window.clearInterval(timer);
        confirmRef.current(step);
      }
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [autoAdvanceActive, awaitingStep, autoAdvanceSeconds]);

  return (
    <section
      className={`cleanup-progress cleanup-progress--${job.status.toLowerCase().replaceAll("_", "-")}`}
      aria-live="polite"
      aria-label={`Serviço de limpeza: ${statusMessage(job)}`}
    >
      <header className="cleanup-progress__header">
        <ActiveIcon className={activeStatuses.has(job.status) ? "cleanup-progress__spinner" : ""} aria-hidden="true" />
        <div>
          <strong>Serviço de limpeza</strong>
          <span>{statusMessage(job)}</span>
        </div>
        <span className="cleanup-progress__percentage">{progress}%</span>
      </header>

      <div
        className="cleanup-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        aria-label={`Progresso da limpeza: ${progress}%`}
      >
        <span style={{ width: `${progress}%` }} />
      </div>

      <div className="cleanup-progress__meta">
        <span>Etapa: <strong>{cleanupStageLabels[job.currentStage]}</strong></span>
        {job.progressTotal > 0 && <span>{job.progressCompleted} de {job.progressTotal} checkpoints</span>}
      </div>

      {steps.length > 0 && (
        <ol className="cleanup-steps" aria-label="Etapas persistidas da limpeza">
          {steps.map((step) => (
            <li className={`cleanup-step cleanup-step--${step.status.toLowerCase().replaceAll("_", "-")}`} key={step.stage}>
              <span className="cleanup-step__icon">{stepIcon(step)}</span>
              <span className="cleanup-step__content">
                <strong>{cleanupStageLabels[step.stage]}</strong>
                <span>{countText(step)}</span>
              </span>
              {step.status === "AWAITING_CONFIRMATION" && <ChevronRight aria-hidden="true" />}
            </li>
          ))}
        </ol>
      )}

      {awaitingStep && !readOnly && (
        <div className="cleanup-progress__decision cleanup-progress__decision--confirm">
          <div>
            <strong>{awaitingStep.totalCount} {cleanupStageLabels[awaitingStep.stage].toLowerCase()} {confirmationAction(awaitingStep.stage)}</strong>
            <span>{awaitingStep.stage === "CENTRALS"
              ? "As centrais e partições permanecerão no tenant; esta etapa apenas desvincula as centrais das contas."
              : awaitingStep.stage === "PARTITIONS"
                ? "As partições permanecerão no tenant; nenhuma delas será excluída nesta etapa."
                : "Confirme para executar apenas esta etapa. O progresso ficará salvo automaticamente."}</span>
            {job.autoAdvancePaused ? (
              <small className="cleanup-progress__countdown cleanup-progress__countdown--paused" role="status">
                Avanço automático pausado. Retome para continuar o fluxo.
              </small>
            ) : (
              <small className="cleanup-progress__countdown" role="status" aria-live="polite">
                Avançando automaticamente em {remaining}s…
              </small>
            )}
          </div>
          <div className="cleanup-progress__decision-actions">
            {job.autoAdvancePaused ? (
              <button
                className="primary-button primary-button--small"
                type="button"
                onClick={() => onToggleAutoAdvance(false)}
                disabled={isBusy}
              >
                <Play aria-hidden="true" />
                {busyAction === "auto-advance:resume" ? "Retomando…" : "Retomar"}
              </button>
            ) : (
              <button
                className="secondary-button secondary-button--small"
                type="button"
                onClick={() => onToggleAutoAdvance(true)}
                disabled={isBusy}
              >
                <Pause aria-hidden="true" />
                {busyAction === "auto-advance:pause" ? "Pausando…" : "Pausar"}
              </button>
            )}
          </div>
        </div>
      )}

      {waitingService && !readOnly && (
        <div className="cleanup-progress__decision cleanup-progress__decision--confirm">
          <div>
            <strong>Limpeza aguardando o Serviço de limpeza</strong>
            <span>Conecte a VPN e valide o acesso ao MySQL. Quando o serviço estiver disponível, retome o processo deste ponto.</span>
          </div>
          <button
            className="primary-button primary-button--small"
            type="button"
            onClick={onResumeJob}
            disabled={isBusy}
          >
            <RefreshCw aria-hidden="true" />
            {busyAction === "resume-job" ? "Retomando…" : "Retomar limpeza"}
          </button>
        </div>
      )}

      {paused && failedItem && (
        <div className="cleanup-progress__decision cleanup-progress__decision--error" role="alert">
          <div>
            <strong>{failedItem.entity === "CENTRAL" || failedItem.entity === "PARTITION"
              ? "Erro no processamento de" : "Erro na exclusão de"} {entityLabels[failedItem.entity] ?? failedItem.entity}</strong>
            <span>ID {failedItem.externalId}{failedItem.errorCode ? ` · ${failedItem.errorCode}` : ""}</span>
            <small>O fluxo está pausado e continuará deste registro após sua decisão.</small>
          </div>
          {!readOnly && (
            <div className="cleanup-progress__decision-actions">
              <button
                className="secondary-button secondary-button--small"
                type="button"
                onClick={() => onRetryItem(failedItem.id)}
                disabled={isBusy}
              >
                <RefreshCw aria-hidden="true" />
                {busyAction === `retry-item:${failedItem.id}` ? "Retomando…" : "Tentar novamente"}
              </button>
              <button
                className="warning-button warning-button--small"
                type="button"
                onClick={() => onIgnoreItem(failedItem.id)}
                disabled={isBusy}
              >
                <SkipForward aria-hidden="true" />
                {busyAction === `ignore-item:${failedItem.id}` ? "Ignorando…" : "Ignorar registro"}
              </button>
            </div>
          )}
        </div>
      )}

      {failed && (
        <div className="cleanup-progress__failure">
          <span>Revise a etapa indicada e tente retomar o mesmo processo persistente.</span>
          {!readOnly && retryEnabled && (
            <button className="secondary-button secondary-button--small" type="button" onClick={onRetryJob} disabled={isBusy}>
              <RefreshCw aria-hidden="true" /> {busyAction === "retry-job" ? "Retomando…" : "Tentar novamente"}
            </button>
          )}
        </div>
      )}

      {withPending && (
        <div className="cleanup-progress__decision cleanup-progress__decision--pending">
          <div>
            <strong>{job.ignoredCount} registro(s) permaneceram pendentes</strong>
            <span>O ciclo POC continua ativo. As pendências ficam registradas para auditoria.</span>
          </div>
          {!readOnly && (
            <button className="danger-button danger-button--small" type="button" onClick={onMarkClean} disabled={isBusy}>
              <ShieldAlert aria-hidden="true" /> Marcar ambiente como limpo
            </button>
          )}
        </div>
      )}
    </section>
  );
}
